import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, Avatar } from '@mui/material';
import { styled } from '@mui/system';
import { getData } from '../api';

interface Team {
  id: number;
  name: string;
}

interface PlayerProfile {
  id: number;
  nom_complet: string;
  equipe: string; // ex: "2,5"
  user_id: number;
  photo?: string;
}

interface Player {
  id: number;
  username: string;
  profile: PlayerProfile;
}

// Styled components
const FieldContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '32px 0',
  width: '100vw',
  maxWidth: '100vw',
  padding: 0,
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    margin: '8px 0',
  },
}));

const Court = styled(Paper)(({ theme }) => ({
  width: '90vw',
  maxWidth: '90vw',
  minHeight: 340,
  background: '#e0e0e0',
  border: '2px solid #1976d2',
  borderTop: "8px solid black",
  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  marginBottom: 32,
  display: 'flex',
  flexWrap: 'wrap',
  alignContent: 'space-between',
  padding: 32,
  boxSizing: 'border-box',
  overflowX: 'auto',
  [theme.breakpoints.down('sm')]: {
    width: '100vw',
    maxWidth: '100vw',
    minHeight: 220,
    padding: 8,
    marginBottom: 12,
  },
}));

const Bench = styled(Paper)(({ theme }) => ({
  width: '100vw',
  maxWidth: '100vw',
  minHeight: 70,
  background: '#f5f5f5',
  border: '1.5px dashed #888',
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '12px 24px',
  gap: 8,
  marginTop: 12,
  boxSizing: 'border-box',
  overflowX: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: '8px 4px',
    minHeight: 90,
    gap: 6,
    marginTop: 8,
  },
}));

const PlayerChip = styled(Box)<{ isDragging?: boolean }>(({ isDragging, theme }) => ({
  padding: '6px 12px',
  background: isDragging ? '#1976d2' : '#fff',
  color: isDragging ? '#fff' : '#1976d2',
  border: '1px solid #1976d2',
  borderRadius: "12px",
  margin: 4,
  cursor: 'grab',
  boxShadow: isDragging ? '0 2px 8px #1976d2aa' : 'none',
  userSelect: 'none',
  fontWeight: 500,
  fontSize: 15,
  textAlign: 'center',
  position: 'relative',
  minWidth: 60,
  minHeight: 36,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: '2px 6px',
    fontSize: 12,
    minWidth: 120,
    minHeight: 28,
    margin: 2,
  },
}));

const PositionSpot = styled(Box)<{ isOver?: boolean }>(({ isOver, theme }) => ({
  width: "100%",
  height: "100%",
  minHeight: 200,
  background: isOver ? '#bbdefb' : '#fafafa',
  border: '2px dashed #1976d2',
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: 12,
  transition: 'background 0.2s',
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    minHeight: 60,
    margin: 4,
  },
}));

// Main component
const VOLLEY_POSITIONS = 6;

const CompoCreate: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [bench, setBench] = useState<Player[]>([]);
  const [positions, setPositions] = useState<(Player | null)[]>(Array(VOLLEY_POSITIONS).fill(null));
  const [draggedPlayer, setDraggedPlayer] = useState<Player | null>(null);

  // Charger équipes et joueurs au chargement
  useEffect(() => {
    getData('teams').then((res: any) => {
      setTeams(res.data || []);
      
      // Charger l'équipe depuis le localStorage après avoir récupéré les équipes
      const storedTeam = localStorage.getItem("selectedTeam");
      if (storedTeam) {
        setSelectedTeamId(Number(storedTeam));
      }
    });
    getData('users', { where: "role = 'player'" }).then(async (res: any) => {
      const users = res.data || [];
      const playersWithProfiles = await Promise.all(
        users.map(async (user: any) => {
          const profileRes = await getData('profiles', { where: `user_id = '${user.id}'` });
            console.log("Profile response:", profileRes.data);
            const profileData = profileRes.data?.[0] || { id: null, nom_complet: '', equipe: '', user_id: user.id, photo: '' };
            const profile = {
            ...profileData,
            photo: profileData.photo || ''
            };
          return { ...user, profile };
        })
      );
      setPlayers(playersWithProfiles);
    });
  }, []);

  // Quand l'équipe change, on met à jour le banc
  useEffect(() => {
    if (selectedTeamId) {
      // Les joueurs de l'équipe sélectionnée
      const teamPlayers = players.filter((p) => (p.profile.equipe || '').split(',').map((id: string) => id.trim()).includes(selectedTeamId.toString()));
      setBench(teamPlayers);
      setPositions(Array(VOLLEY_POSITIONS).fill(null));
    } else {
      setBench([]);
      setPositions(Array(VOLLEY_POSITIONS).fill(null));
    }
  }, [selectedTeamId, players]);

  // Drag and drop handlers (desktop + mobile)
  const onDragStart = (player: Player) => {
    setDraggedPlayer(player);
  };
  const onDragEnd = () => {
    setDraggedPlayer(null);
  };
  // Touch events for mobile drag
  const [touchDragPlayer, setTouchDragPlayer] = useState<Player | null>(null);
  const [touchOverIdx, setTouchOverIdx] = useState<number | null>(null);

  const onTouchStart = (player: Player) => {
    setTouchDragPlayer(player);
  };
  const onTouchEnd = () => {
    if (touchDragPlayer !== null && touchOverIdx !== null) {
      onDropToPositionMobile(touchOverIdx, touchDragPlayer);
    }
    setTouchDragPlayer(null);
    setTouchOverIdx(null);
  };
  const onDropToPosition = (idx: number) => {
    if (!draggedPlayer) return;
    setPositions((prev) => {
      const current = prev[idx];
      // Si on droppe sur une autre position du terrain occupée, on swap
      if (current && current.id !== draggedPlayer.id) {
        // Chercher la position actuelle du draggedPlayer
        const fromIdx = prev.findIndex((p) => p && p.id === draggedPlayer.id);
        if (fromIdx !== -1) {
          const newPositions = [...prev];
          newPositions[idx] = draggedPlayer;
          newPositions[fromIdx] = current;
          return newPositions;
        }
      }
      // Sinon, comportement normal (banc -> terrain ou terrain -> terrain vide)
      let newPositions = prev.map((p) => (p && p.id === draggedPlayer.id ? null : p));
      newPositions[idx] = draggedPlayer;
      return newPositions;
    });
    setBench((prev) => prev.filter((p) => p.id !== draggedPlayer.id));
    setDraggedPlayer(null);
  };
  const onDropToPositionMobile = (idx: number, player: Player) => {
    setPositions((prev) => {
      const newPositions = prev.map((p) => (p && p.id === player.id ? null : p));
      newPositions[idx] = player;
      return newPositions;
    });
    setBench((prev) => prev.filter((p) => p.id !== player.id));
  };
  const onDropToBench = () => {
    if (!draggedPlayer) return;
    setPositions((prev) => prev.map((p) => (p && p.id === draggedPlayer.id ? null : p)));
    setBench((prev) => {
      if (prev.find((p) => p.id === draggedPlayer.id)) return prev;
      return [...prev, draggedPlayer];
    });
    setDraggedPlayer(null);
  };
  // Touch drop to bench
  const onDropToBenchMobile = (player: Player) => {
    setPositions((prev) => prev.map((p) => (p && p.id === player.id ? null : p)));
    setBench((prev) => {
      if (prev.find((p) => p.id === player.id)) return prev;
      return [...prev, player];
    });
  };

  // Sauvegarde et chargement localStorage
  const saveCompo = () => {
    if (!selectedTeamId) return;
    const msg = 'Attention : la précédente composition sauvegardée va être écrasée. Continuer ?';
    if (!window.confirm(msg)) return;
    const compo = {
      selectedTeamId,
      positions: positions.map(p => p ? p.id : null),
      bench: bench.map(p => p.id),
    };
    localStorage.setItem('volley-compo', JSON.stringify(compo));
  };

  const loadCompo = () => {
    const str = localStorage.getItem('volley-compo');
    if (!str) return;
    try {
      const compo = JSON.parse(str);
      if (!compo.selectedTeamId) return;
      setSelectedTeamId(compo.selectedTeamId);
      // On attend que players soit bien chargé (useEffect dépend de selectedTeamId)
      setTimeout(() => {
        // On retrouve les bons objets Player par id
        setPositions(compo.positions.map((id: number|null) => id ? players.find((p) => p.id === id) || null : null));
        setBench(compo.bench.map((id: number) => players.find((p) => p.id === id)).filter(Boolean));
      }, 100);
    } catch {}
  };

  return (
    <Box sx={{ width: '100vw', maxWidth: '100vw', mx: 0, mt: 4, px: 0 }}>
      <Typography variant="h4" gutterBottom>
        Composer une équipe
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1">Choisir une équipe :</Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          {teams.map((team: Team) => (
            <Button
              key={team.id}
              variant={selectedTeamId === team.id ? 'contained' : 'outlined'}
              onClick={() => setSelectedTeamId(team.id)}
            >
              {team.name}
            </Button>
          ))}
        </Box>        
      </Box>
      <FieldContainer>
        <Court elevation={3}>
          {/* Disposition terrain volley :
              [4][3][2] (derrière)
              [5][1][1] (devant, 1 = bas centre et droite)
          */}
          <Box sx={{
            display: 'grid',
            gridTemplateRows: { xs: 'repeat(2, 1fr)', sm: '1fr 1fr' },
            gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)' },
            gap: { xs: 0.5, sm: 1 },
            justifyItems: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            minWidth: { xs: 320, sm: 0 },
            overflowX: { xs: 'auto', sm: 'visible' },
          }}>
            {/* Ligne du fond : 4 3 2 (row 1) */}
            {[3,2,1].map((poste, i) => (
              <PositionSpot
                key={poste}
                isOver={!!draggedPlayer && !positions[poste]}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropToPosition(poste)}
                onDragEnter={(e) => e.preventDefault()}
                // Touch events
                onTouchStart={touchDragPlayer ? undefined : (e) => { e.stopPropagation(); }}
                onTouchMove={touchDragPlayer ? (e) => { setTouchOverIdx(poste); } : undefined}
                onTouchEnd={touchDragPlayer ? onTouchEnd : undefined}
                sx={{ gridRow: 1, gridColumn: i+1, position: 'relative', border: touchOverIdx === poste ? '2px solid #1976d2' : undefined }}
              >
                {positions[poste] ? (
                  <PlayerChip
                    draggable
                    onDragStart={() => onDragStart(positions[poste]!)}
                    onDragEnd={onDragEnd}
                    // Touch events
                    onTouchStart={() => onTouchStart(positions[poste]!)}
                    onTouchEnd={onTouchEnd}
                  >
                    <Avatar
                      src={positions[poste]!.profile.photo || undefined}
                      sx={{ width: { xs: 20, sm: 28 }, height: { xs: 20, sm: 28 }, fontSize: { xs: 11, sm: 15 }, mr: 1 }}
                    >
                      {(!positions[poste]!.profile.photo && (positions[poste]!.profile.nom_complet || positions[poste]!.username))
                        ? (positions[poste]!.profile.nom_complet || positions[poste]!.username)
                            .split(' ')
                            .map((n: string) => n[0])
                            .join('')
                            .toUpperCase()
                        : null}
                    </Avatar>
                    <br />
                    {positions[poste]!.profile.nom_complet || positions[poste]!.username}
                    <Box
                      sx={{ position: 'absolute', top: 2, right: 2, cursor: 'pointer', color: '#d32f2f', zIndex: 2, fontSize: 18, lineHeight: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setBench((prev) => prev.find((p) => p.id === positions[poste]!.id) ? prev : [...prev, positions[poste]!]);
                        setPositions((prev) => prev.map((p, idx) => idx === poste ? null : p));
                      }}
                      title="Remettre sur le banc"
                    >
                      ✕
                    </Box>
                  </PlayerChip>
                ) : null}
                {/* Numéro de poste */}
                <Typography variant="caption" sx={{ position: 'absolute', bottom: 2, right: 4, fontWeight: 600, color: '#1976d2', fontSize: 13 }}>
                  {poste+1}
                </Typography>
                {!positions[poste] && (
                  <Typography variant="caption" color="textSecondary">
                    {`Poste ${poste+1}`}
                  </Typography>
                )}
              </PositionSpot>
            ))}
            {/* Ligne devant : 5 6 1 (row 2) */}
            {[4,5,0].map((poste, i) => (
              <PositionSpot
                key={poste}
                isOver={!!draggedPlayer && !positions[poste]}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropToPosition(poste)}
                onDragEnter={(e) => e.preventDefault()}
                // Touch events
                onTouchStart={touchDragPlayer ? undefined : (e) => { e.stopPropagation(); }}
                onTouchMove={touchDragPlayer ? (e) => { setTouchOverIdx(poste); } : undefined}
                onTouchEnd={touchDragPlayer ? onTouchEnd : undefined}
                sx={{ gridRow: 2, gridColumn: i+1, position: 'relative', border: touchOverIdx === poste ? '2px solid #1976d2' : undefined }}
              >
                {positions[poste] ? (
                  <PlayerChip
                    draggable
                    onDragStart={() => onDragStart(positions[poste]!)}
                    onDragEnd={onDragEnd}
                    // Touch events
                    onTouchStart={() => onTouchStart(positions[poste]!)}
                    onTouchEnd={onTouchEnd}
                  >
                    <Avatar
                      src={positions[poste]!.profile.photo || undefined}
                      sx={{ width: { xs: 20, sm: 28 }, height: { xs: 20, sm: 28 }, fontSize: { xs: 11, sm: 15 }, mr: 1 }}
                    >
                      {(!positions[poste]!.profile.photo && (positions[poste]!.profile.nom_complet || positions[poste]!.username))
                        ? (positions[poste]!.profile.nom_complet || positions[poste]!.username)
                            .split(' ')
                            .map((n: string) => n[0])
                            .join('')
                            .toUpperCase()
                        : null}
                    </Avatar>
                    {positions[poste]!.profile.nom_complet || positions[poste]!.username}
                    <Box
                      sx={{ position: 'absolute', top: 2, right: 2, cursor: 'pointer', color: '#d32f2f', zIndex: 2, fontSize: 18, lineHeight: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setBench((prev) => prev.find((p) => p.id === positions[poste]!.id) ? prev : [...prev, positions[poste]!]);
                        setPositions((prev) => prev.map((p, idx) => idx === poste ? null : p));
                      }}
                      title="Remettre sur le banc"
                    >
                      ✕
                    </Box>
                  </PlayerChip>
                ) : null}
                {/* Numéro de poste */}
                <Typography variant="caption" sx={{ position: 'absolute', bottom: 2, right: 4, fontWeight: 600, color: '#1976d2', fontSize: 13 }}>
                  {poste+1}
                </Typography>
                {!positions[poste] && (
                  <Typography variant="caption" color="textSecondary">
                    {`Poste ${poste+1}`}
                  </Typography>
                )}
              </PositionSpot>
            ))}
          </Box>
        </Court>
        <Bench elevation={1}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDropToBench}
          // Touch events
          onTouchMove={touchDragPlayer ? (e) => { setTouchOverIdx(-1); } : undefined}
          onTouchEnd={touchDragPlayer ? () => { if (touchDragPlayer) { onDropToBenchMobile(touchDragPlayer); setTouchDragPlayer(null); setTouchOverIdx(null); } } : undefined}
        >
          <Typography variant="subtitle2" sx={{ mr: 2, minWidth: 60 }}>
            Banc
          </Typography>
          {bench.length === 0 && (
            <Typography variant="caption" color="textSecondary">Aucun joueur</Typography>
          )}
          {bench.map((player) => (
            <PlayerChip
              key={player.id}
              draggable
              onDragStart={() => onDragStart(player)}
              onDragEnd={onDragEnd}
              // Touch events
              onTouchStart={() => onTouchStart(player)}
              onTouchEnd={onTouchEnd}
            >
              <Avatar
                src={player.profile.photo || undefined}
                sx={{ width: { xs: 20, sm: 28 }, height: { xs: 20, sm: 28 }, fontSize: { xs: 11, sm: 15 }, mr: 1 }}
              >
                {(!player.profile.photo && (player.profile.nom_complet || player.username))
                  ? (player.profile.nom_complet || player.username)
                      .split(' ')
                      .map((n: string) => n[0])
                      .join('')
                      .toUpperCase()
                  : null}
              </Avatar>
              {player.profile.nom_complet || player.username}
            </PlayerChip>
          ))}
        </Bench>
      </FieldContainer>
      {/* Non convoqués */}
      {selectedTeamId && (
        <Bench elevation={1}
          sx={{ mt: 2, background: '#f9f9f9', borderColor: '#bbb' }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            if (!draggedPlayer) return;
            setBench((prev) => prev.filter((p) => p.id !== draggedPlayer.id));
            setPositions((prev) => prev.map((p) => (p && p.id === draggedPlayer.id ? null : p)));
            setDraggedPlayer(null);
          }}
          // Touch events
          onTouchMove={touchDragPlayer ? (e) => { setTouchOverIdx(-2); } : undefined}
          onTouchEnd={touchDragPlayer ? () => { if (touchDragPlayer) {
            // Retirer le joueur du banc et du terrain
            setBench((prev) => prev.filter((p) => p.id !== touchDragPlayer.id));
            setPositions((prev) => prev.map((p) => (p && p.id === touchDragPlayer.id ? null : p)));
            setTouchDragPlayer(null);
            setTouchOverIdx(null);
          }} : undefined}
        >
          <Typography variant="subtitle2" sx={{ mr: 2, minWidth: 120, color: '#b71c1c' }}>
            Non convoqués
          </Typography>
          {(() => {
            // Joueurs de l'équipe qui ne sont ni sur le banc ni sur le terrain
            const idsOnBench = new Set(bench.map(p => p.id));
            const idsOnField = new Set(positions.filter(Boolean).map(p => p!.id));
            const teamPlayers = players.filter((p) => (p.profile.equipe || '').split(',').map((id: string) => id.trim()).includes(selectedTeamId.toString()));
            const nonConvoques = teamPlayers.filter(p => !idsOnBench.has(p.id) && !idsOnField.has(p.id));
            if (nonConvoques.length === 0) {
              return <Typography variant="caption" color="textSecondary">Aucun joueur</Typography>;
            }
            return nonConvoques.map((player) => (
              <PlayerChip
                key={player.id}
                draggable
                onDragStart={() => onDragStart(player)}
                onDragEnd={onDragEnd}
                // Touch events
                onTouchStart={() => onTouchStart(player)}
                onTouchEnd={onTouchEnd}
              >
                <Avatar
                  src={player.profile.photo || undefined}
                  sx={{ width: { xs: 20, sm: 28 }, height: { xs: 20, sm: 28 }, fontSize: { xs: 11, sm: 15 }, mr: 1 }}
                >
                  {(!player.profile.photo && (player.profile.nom_complet || player.username))
                    ? (player.profile.nom_complet || player.username)
                        .split(' ')
                        .map((n: string) => n[0])
                        .join('')
                        .toUpperCase()
                    : null}
                </Avatar>
                {player.profile.nom_complet || player.username}
              </PlayerChip>
            ));
          })()}
        </Bench>
      )}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, textAlign: 'right', justifyContent: 'center', marginTop: 4 }}>
          <Button variant="contained" color="primary" onClick={saveCompo} disabled={!selectedTeamId}>
            Sauvegarder la compo
          </Button>
          <Button variant="contained" color="secondary" onClick={loadCompo}>
            Charger la dernière compo
          </Button>
        </Box>
    </Box>
  );
};

export default CompoCreate;
